import './tax-report-form.scss'

import { DOWNLOAD_API } from 'api/download-api'
import { useGetByTenantCounterpartyCounterpartyidsQuery } from 'api/rtk-api'
import { LegacyDatePicker, Section } from 'components/core-v2'
import { CheckboxWithLabel, RadioWithLabel, Tabs } from 'components/core-v2/molecules'
import { DownloadLink, onDownloadLinkClick } from 'components/core-v2/molecules/download-link'
import { DateRangePicker, last7Days, last30Days, last90Days } from 'components/core-v2/organisms'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { formatToDatePartOnly } from 'utilities/date'

import { TENANT, TODAY } from '../../../const'
import { COPY } from '../constants'

export const TaxReportForm = () => {
  function getDateAsString(date: Date) {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
  }

  const { data: counterpartyIds } = useGetByTenantCounterpartyCounterpartyidsQuery({
    tenant: TENANT
  })
  const [selectedCounterpartyIds, setSelectedCounterpartyIds] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState('TaxReports')
  const [selectedPeriodEndDate, setSelectedPeriodEndDate] = useState(new Date(2022, 1, 28))
  const [startDate, setStartDate] = useState(formatToDatePartOnly(TODAY.toDateString()))
  const [endDate, setEndDate] = useState(formatToDatePartOnly(TODAY.toDateString()))
  const [taxReportDate, setTaxReportDate] = useState(getDateAsString(new Date()))
  const [taxReportType, setTaxReportType] = useState('ByPeriod')

  const addOrRemoveCounterpartyId = (counterpartyId: string) => {
    if (!selectedCounterpartyIds.includes(counterpartyId)) {
      const newList = selectedCounterpartyIds.concat([counterpartyId])
      setSelectedCounterpartyIds(newList)
    } else {
      const newList = selectedCounterpartyIds.filter(x => x !== counterpartyId)
      setSelectedCounterpartyIds(newList)
    }
  }

  const periodEndDates: Date[] = []
  const firstReportDate = new Date(2022, 1, 28)
  const curFebDate = firstReportDate
  const curAugDate = new Date(2022, 7, 31)
  while (curFebDate <= TODAY || curAugDate <= TODAY) {
    if (curFebDate < curAugDate) {
      periodEndDates.push(new Date(curFebDate))
      curFebDate.setFullYear(curFebDate.getFullYear() + 1)
    } else {
      periodEndDates.push(new Date(curAugDate))
      curAugDate.setFullYear(curAugDate.getFullYear() + 1)
    }
  }

  const setPeriodEndDate = (date: Date) => {
    setSelectedPeriodEndDate(date)
  }

  const form = useForm()
  const dateRange = form.watch('tax_report_date_picker')

  if (
    dateRange !== undefined &&
    selectedTab === 'NoteReport' &&
    !(dateRange[0] instanceof Date) &&
    (startDate !== dateRange[0].format('YYYY-MM-DD') ||
      endDate !== dateRange[1].format('YYYY-MM-DD'))
  ) {
    setStartDate(dateRange[0].format('YYYY-MM-DD'))
    setEndDate(dateRange[1].format('YYYY-MM-DD'))
  }

  const setPeriodEndTab = () => {
    setPeriodEndDate(firstReportDate)
    setSelectedTab('TaxReports')
  }

  const getReportPeriodDisplay = (date: Date) => {
    if (date.getMonth() === 1) {
      return `${date.getFullYear() - 1} / ${date.getFullYear()} Full`
    }
    return `${date.getFullYear()} / ${date.getFullYear() + 1} Interim`
  }

  const getReportPeriodURL = (date: Date, reportType: string) => {
    if (date.getMonth() === 1) {
      return DOWNLOAD_API.exportFullTaxReport(date.getFullYear().toString(), reportType)
    }
    return DOWNLOAD_API.exportInterimTaxReport(date.getFullYear().toString(), reportType)
  }

  return (
    <Section type="primary">
      <Section mb="6" type="primary">
        <Tabs.List>
          <Tabs.Tab
            isSelected={selectedTab === 'TaxReports'}
            value="tax-reports"
            onClick={() => setPeriodEndTab()}
          >
            Tax Reports
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'NoteReport'}
            value="note-report"
            onClick={() => setSelectedTab('NoteReport')}
          >
            Note Report
          </Tabs.Tab>
        </Tabs.List>
        {selectedTab === 'TaxReports' && (
          <Tabs.Panel value="tax-reports">
            <>
              <Section mb="6" mt="4" mx="6" type="primary">
                <strong>Select tax report type</strong>
                <Section mt="4" type="primary">
                  <RadioWithLabel
                    checked={taxReportType === 'ByPeriod'}
                    onChange={() => setTaxReportType('ByPeriod')}
                  >
                    By period
                  </RadioWithLabel>
                  <RadioWithLabel
                    checked={taxReportType === 'ByDate'}
                    onChange={() => setTaxReportType('ByDate')}
                  >
                    By date
                  </RadioWithLabel>
                </Section>
              </Section>
              {taxReportType === 'ByPeriod' && (
                <Section mb="4" mt="4" mx="6" type="primary">
                  <strong>Tax reporting periods </strong>
                  <Section mt="2" type="primary">
                    {periodEndDates.map(date => (
                      <Section key={date.toDateString()} mt="2" type="primary">
                        <RadioWithLabel
                          checked={selectedPeriodEndDate.toISOString() === date.toISOString()}
                          onChange={() => setPeriodEndDate(date)}
                        >
                          {getReportPeriodDisplay(date)}
                        </RadioWithLabel>
                      </Section>
                    ))}
                  </Section>
                </Section>
              )}
              {taxReportType === 'ByDate' && (
                <Section mb="4" mt="4" mx="6" type="primary">
                  <strong>Select tax report date </strong>
                  <Section mt="2" type="primary">
                    <LegacyDatePicker
                      maxDate={TODAY}
                      minDate={new Date(2022, 1, 28)}
                      value={new Date(taxReportDate)}
                      onChange={date => setTaxReportDate(getDateAsString(date))}
                    />
                  </Section>
                </Section>
              )}
            </>
          </Tabs.Panel>
        )}

        {selectedTab === 'NoteReport' && (
          <Tabs.Panel className="note-report-wrapper" value="note-report">
            <Section mb="4" mt="4" mx="6" type="primary">
              <FormProvider {...form}>
                <DateRangePicker
                  showClearDatesButton
                  defaultValue={last7Days.value}
                  id="tax_report_date_picker"
                  isPopover={false}
                  speedDials={[last7Days, last30Days, last90Days]}
                />
              </FormProvider>
            </Section>
            <Section mb="6" mx="6" type="primary">
              <strong>Counterparties</strong>
              <Section type="primary">
                {counterpartyIds.map(counterpartyId => (
                  <Section key={counterpartyId} mt="2" type="primary">
                    <CheckboxWithLabel
                      checked={selectedCounterpartyIds.includes(counterpartyId)}
                      onChange={() => addOrRemoveCounterpartyId(counterpartyId)}
                    >
                      {counterpartyId}
                    </CheckboxWithLabel>
                  </Section>
                ))}
              </Section>
            </Section>
          </Tabs.Panel>
        )}
      </Section>
      {selectedTab === 'NoteReport' && (
        <Tabs.Panel value="note-report">
          <Section mx="6" type="primary">
            <DownloadLink
              onClick={() =>
                onDownloadLinkClick({
                  url: DOWNLOAD_API.exportNoteReport(
                    selectedCounterpartyIds.join(','),
                    startDate,
                    endDate,
                    100000
                  )
                })
              }
            >
              {COPY.exportTaxReport}
            </DownloadLink>
          </Section>
        </Tabs.Panel>
      )}
      {selectedTab === 'TaxReports' && taxReportType === 'ByDate' && (
        <Tabs.Panel value="tax-reports">
          <Section mx="6" type="primary">
            <DownloadLink
              onClick={() =>
                onDownloadLinkClick({
                  url: DOWNLOAD_API.exportIT3BbyDate(taxReportDate)
                })
              }
            >
              {COPY.exportIT3B}
            </DownloadLink>
          </Section>
          <Section mx="6" type="primary">
            <DownloadLink
              onClick={() =>
                onDownloadLinkClick({
                  url: DOWNLOAD_API.exportIT3CbyDate(taxReportDate)
                })
              }
            >
              {COPY.exportIT3C}
            </DownloadLink>
          </Section>
        </Tabs.Panel>
      )}
      {selectedTab === 'TaxReports' && taxReportType === 'ByPeriod' && (
        <Tabs.Panel value="tax-reports">
          <Section mx="6" type="primary">
            <DownloadLink
              onClick={() =>
                onDownloadLinkClick({
                  url: getReportPeriodURL(selectedPeriodEndDate, 'it3b')
                })
              }
            >
              {COPY.exportIT3B}
            </DownloadLink>
          </Section>
          <Section mx="6" type="primary">
            <DownloadLink
              onClick={() =>
                onDownloadLinkClick({
                  url: getReportPeriodURL(selectedPeriodEndDate, 'it3c')
                })
              }
            >
              {COPY.exportIT3C}
            </DownloadLink>
          </Section>
        </Tabs.Panel>
      )}
    </Section>
  )
}
